import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/core';
import Layout from '../../../components/Layout';
import {
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import {
  AdminEventRead,
  AdminPostOrderBy,
  GetAdminEventsParams,
  Order,
  Pagination as IPagination,
  Role,
} from '../../../client/interfaces';
import { AdminPostFilter, Button, Pagination, EventTable } from '../../../components';
import client from '../../../client/client';
import { useMessage } from '../../../contexts/Messages/MessageContext';

export const emptyGetAdminEventsParams: GetAdminEventsParams = {
  orderBy: AdminPostOrderBy.createdAtUtc,
  order: Order.DESC,
  search: '',
  page: 1,
  status: undefined,
  tags: [],
};

const AdminEventListScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminEventListScreen>
> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const { me, hasRole } = useAuth();
  const { setMessage } = useMessage();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [getAdminEventsParams, setGetAdminEventsParams] = useState<GetAdminEventsParams>({
    ...emptyGetAdminEventsParams,
    ...route.params,
  });

  const [adminEvents, setAdminEvents] = useState<AdminEventRead[]>([]);
  const [pagination, setPagination] = useState<IPagination>();

  const getAdminEventsParamsJSON = JSON.stringify(getAdminEventsParams);

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.publisher)) {
        navigation.navigate(ScreenName.HomeScreen);
      } else {
        setGetAdminEventsParams(emptyGetAdminEventsParams);
      }
    }, [me?.roleList]),
  );

  const fetchAdminEvents = async () => {
    setIsLoading(true);

    try {
      const response = await client.getAdminEvents(getAdminEventsParams);
      setAdminEvents(response.items);
      setPagination(response.pagination);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setMessage({ message: e.message, type: 'error' });
    }

    setIsLoading(false);
  };

  useFocusEffect(
    useCallback(() => {
      fetchAdminEvents();
    }, [getAdminEventsParamsJSON]),
  );

  useEffect(() => {
    setGetAdminEventsParams((prev) => ({ ...prev, ...route.params }));
  }, [route.params]);

  return (
    <Layout title={t('admin:event_list:title')} titleMarginBottom={false}>
      <Button
        text={t('admin:event_list:add_new')}
        variant={'fill'}
        onPress={() => navigation.navigate(ScreenName.AdminEventEditScreen)}
        size={'medium'}
        style={{ alignItems: 'flex-end', marginBottom: 40 }}
      />
      <AdminPostFilter
        params={getAdminEventsParams}
        paramSetter={setGetAdminEventsParams}
        pagination={pagination}
      />
      <EventTable
        rows={adminEvents}
        paramSetter={setGetAdminEventsParams}
        params={getAdminEventsParams}
        refresh={fetchAdminEvents}
        isLoading={isLoading}
      />
      {pagination && (
        <Pagination
          data={pagination}
          paramSetter={setGetAdminEventsParams}
          params={getAdminEventsParams}
        />
      )}
    </Layout>
  );
};

export default AdminEventListScreen;
